import { useCallback } from "react";

import { useParams } from "react-router";
import { SyncLoader } from "react-spinners";
import { AnalyticsDataSource, Metadata } from "@doitintl/cmp-models";
import { Box, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { DraggableContainer } from "../../../../Components/DragAndDrop/DraggableContainer";
import { DraggableGroup } from "../../../../Components/DragAndDrop/DraggableGroup";
import { useAnalyticsDimensions } from "../../../../Components/hooks/cloudAnalytics/useAnalyticsDimensions/useAnalyticsDimensions";
import { useCloudAnalyticsMetadata } from "../../../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { useAuthContext } from "../../../../Context/AuthContext";
import useSegmentTrackEvent from "../../../../utils/useSegmentTrackEvent";
import AllocationSegment from "../AllocationSegment";
import useAllocationDndStore from "../hooks/useAllocationDnd";
import { useAllocationQueryRun } from "../hooks/useAllocationQueryRun";
import { UnallocatedCosts } from "../UnallocatedCosts";
import { trackAllocationEvent } from "../utils";
import { AllocationCalculation } from "./AllocationDraggableItem/AllocationCalculation/AllocationCalculation";
import { AllocationDraggableItem } from "./AllocationDraggableItem/AllocationDraggableItem";

type Props = {
  disabled: boolean;
};

export function AllocationDragAndDrop({ disabled }: Props) {
  const { items, setItems, hasUnallocated, unallocatedCosts } = useAllocationDndStore();
  const theme = useTheme();
  const { trackEvent } = useSegmentTrackEvent();
  const { metadata, hasDataHub } = useCloudAnalyticsMetadata();
  const { allocationId } = useParams<{ allocationId: string }>();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const { queryRunning, queryResults } = useAllocationQueryRun();
  const { dimensions = [], handleDimensionLabelsChange } = useAnalyticsDimensions({
    metadataSnapshots: metadata,
    defaultDataSource: hasDataHub ? AnalyticsDataSource.BILLING_DATAHUB : AnalyticsDataSource.BILLING,
    excludeMetadataTypes: [Metadata.ATTRIBUTION_GROUP],
  });

  const handleItemsOrderChanged = useCallback(
    (newItems) => {
      setItems(newItems);
      trackAllocationEvent({
        trackEvent,
        eventName: "Allocation Rule Reordered",
        ruleCount: items.main.length,
        hasUnallocated,
        allocationId,
        currentUserEmail: currentUser.email,
      });
    },
    [setItems, trackEvent, items.main.length, hasUnallocated, allocationId, currentUser.email]
  );

  const renderDraggingItem = useCallback(
    (id: string) => (
      <AllocationDraggableItem
        grabbing
        initialAttributionID={id}
        dimensions={dimensions}
        handleDimensionLabelsChange={handleDimensionLabelsChange}
      />
    ),
    [dimensions, handleDimensionLabelsChange]
  );

  const renderContent = useCallback(
    (items: string[]) => (
      <>
        {items.map((id, index) => (
          <AllocationDraggableItem
            key={id}
            disabled={disabled}
            queryRunning={queryRunning}
            queryResults={queryResults}
            allocationRuleIndex={index}
            initialAttributionID={id}
            handleDimensionLabelsChange={handleDimensionLabelsChange}
            dimensions={dimensions}
          />
        ))}
      </>
    ),
    [dimensions, disabled, handleDimensionLabelsChange, queryResults, queryRunning]
  );

  if (items.main?.length === 0 || dimensions.length === 0) {
    return (
      <Stack width="100%" alignItems="center">
        <SyncLoader size={10} color={theme.palette.primary.main} loading />
      </Stack>
    );
  }

  return (
    <Stack sx={{ width: "100%" }}>
      <Stack>
        <DraggableContainer
          values={items}
          renderDraggingItem={renderDraggingItem}
          onItemsOrderChanged={handleItemsOrderChanged}
        >
          <Stack direction="column">
            <DraggableGroup id="main" renderContent={renderContent} />
          </Stack>
        </DraggableContainer>
      </Stack>
      {hasUnallocated && (
        <Stack gap={2}>
          <UnallocatedCosts disabled={disabled} />
          <Box sx={{ ml: 6 }}>
            <AllocationCalculation
              queryRunning={queryRunning}
              queryResults={queryResults}
              resourceName={unallocatedCosts}
            />
          </Box>
        </Stack>
      )}
      {!hasUnallocated && <AllocationSegment disabled={disabled} />}
    </Stack>
  );
}
